import { Autocomplete, Box, Button, Chip, MenuItem, Popover, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useReferentielijst } from '../../api/referentie.ts';
import type { Optie } from '../../helpers/labels.ts';
import {
  type Eigenschap,
  type Eigenschapfilter,
  type Filtermodus,
  type Filterstand,
  legeFilterstand,
  samenvatting,
} from './kandidaatEigenschappen.ts';

const waardemodusLabels: Record<Eigenschapfilter['soort'], string> = {
  tekst: 'Bevat',
  keuze: 'Is een van',
  referentie: 'Is een van',
  datum: 'Ligt tussen',
};

interface PopoverProps {
  eigenschap: Eigenschap;
  stand: Filterstand | null;
  anker: HTMLElement;
  onToepassen: (stand: Filterstand | null) => void;
  onSluit: () => void;
}

/**
 * Een venster om het filter op één eigenschap in te stellen. Het filter geldt pas na Toepassen.
 * @param props De eigenschap, de huidige stand, waar het venster hangt en wat er bij toepassen en sluiten gebeurt.
 * @returns Het venster.
 */
export function FilterPopover({ eigenschap, stand, anker, onToepassen, onSluit }: PopoverProps) {
  return (
    <Popover
      open
      anchorEl={anker}
      onClose={onSluit}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ transition: { onEntered: focusEersteVeld } }}
    >
      <Filterformulier
        eigenschap={eigenschap}
        stand={stand}
        onToepassen={(nieuw) => {
          onToepassen(nieuw);
          onSluit();
        }}
      />
    </Popover>
  );
}

/**
 * Zet de focus op het invoerveld van het filter, of anders op de voorwaarde. Het venster zelf pakt bij openen de
 * focus, dus `autoFocus` op een veld werkt hier niet.
 * @param venster Het geopende venster.
 */
function focusEersteVeld(venster: HTMLElement) {
  const veld =
    venster.querySelector<HTMLElement>('[data-waardeveld] input') ??
    venster.querySelector<HTMLElement>('[role="combobox"]');
  veld?.focus();
}

function Filterformulier({
  eigenschap,
  stand,
  onToepassen,
}: {
  eigenschap: Eigenschap;
  stand: Filterstand | null;
  onToepassen: (stand: Filterstand | null) => void;
}) {
  const [concept, setConcept] = useState<Filterstand>(stand ?? legeFilterstand);
  const zet = (wijziging: Partial<Filterstand>) => setConcept((huidig) => ({ ...huidig, ...wijziging }));
  const filter = eigenschap.filter;
  if (!filter) {
    return null;
  }
  return (
    <Box
      component="form"
      sx={{ p: 2, width: 340, maxWidth: '100vw' }}
      onSubmit={(gebeurtenis) => {
        gebeurtenis.preventDefault();
        onToepassen(concept);
      }}
    >
      <Stack spacing={2}>
        <Typography variant="subtitle2">Filter op {eigenschap.label.toLowerCase()}</Typography>
        {eigenschap.optioneel && (
          <TextField
            select
            size="small"
            label="Voorwaarde"
            value={concept.modus}
            onChange={(gebeurtenis) => zet({ modus: gebeurtenis.target.value as Filtermodus })}
          >
            <MenuItem value="waarde">{waardemodusLabels[filter.soort]}</MenuItem>
            <MenuItem value="leeg">Is leeg</MenuItem>
            <MenuItem value="gevuld">Is ingevuld</MenuItem>
          </TextField>
        )}
        {concept.modus === 'waarde' && (
          <Box data-waardeveld>
            <Waardeveld filter={filter} concept={concept} zet={zet} />
          </Box>
        )}
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
          <Button disabled={!stand} onClick={() => onToepassen(null)}>
            Wissen
          </Button>
          <Button type="submit" variant="contained">
            Toepassen
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

function Waardeveld({
  filter,
  concept,
  zet,
}: {
  filter: Eigenschapfilter;
  concept: Filterstand;
  zet: (wijziging: Partial<Filterstand>) => void;
}) {
  switch (filter.soort) {
    case 'tekst':
      return (
        <TextField
          size="small"
          label="Bevat"
          fullWidth
          value={concept.tekst}
          onChange={(gebeurtenis) => zet({ tekst: gebeurtenis.target.value })}
        />
      );
    case 'keuze':
      return (
        <Meerkeuze
          opties={filter.opties}
          keuzes={concept.keuzes}
          onChange={(keuzes) => zet({ keuzes })}
          label={waardemodusLabels.keuze}
        />
      );
    case 'referentie':
      return <ReferentieMeerkeuze filter={filter} keuzes={concept.keuzes} onChange={(keuzes) => zet({ keuzes })} />;
    case 'datum':
      return (
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            type="date"
            label="Van"
            value={concept.van}
            onChange={(gebeurtenis) => zet({ van: gebeurtenis.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
          <TextField
            size="small"
            type="date"
            label="Tot en met"
            value={concept.tot}
            onChange={(gebeurtenis) => zet({ tot: gebeurtenis.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
        </Stack>
      );
    default:
      return null;
  }
}

function Meerkeuze({
  opties,
  keuzes,
  onChange,
  label,
  laden = false,
}: {
  opties: Optie<string>[];
  keuzes: string[];
  onChange: (keuzes: string[]) => void;
  label: string;
  laden?: boolean;
}) {
  // Een gekozen waarde die niet (meer) in de lijst staat, blijft zichtbaar met zijn id als label.
  const gekozen = keuzes.map(
    (keuze) => opties.find((optie) => optie.waarde === keuze) ?? { waarde: keuze, label: keuze },
  );
  return (
    <Autocomplete<Optie<string>, true>
      multiple
      disableCloseOnSelect
      options={opties}
      value={gekozen}
      onChange={(_gebeurtenis, waarden) => onChange(waarden.map((optie) => optie.waarde))}
      getOptionLabel={(optie) => optie.label}
      isOptionEqualToValue={(a, b) => a.waarde === b.waarde}
      loading={laden}
      size="small"
      renderInput={(parameters) => <TextField {...parameters} label={label} />}
    />
  );
}

function ReferentieMeerkeuze({
  filter,
  keuzes,
  onChange,
}: {
  filter: Extract<Eigenschapfilter, { soort: 'referentie' }>;
  keuzes: string[];
  onChange: (keuzes: string[]) => void;
}) {
  const query = useReferentielijst(filter.lijst);
  const opties = (query.data ?? []).map((item) => ({ waarde: item.id, label: item.naam }));
  return (
    <Meerkeuze
      opties={opties}
      keuzes={keuzes}
      onChange={onChange}
      label={waardemodusLabels.referentie}
      laden={query.isPending}
    />
  );
}

interface ChipProps {
  eigenschap: Eigenschap;
  stand: Filterstand;
  onKlik: (anker: HTMLElement) => void;
  onWis: () => void;
}

/**
 * Een actief filter als chip: klik om het aan te passen, het kruisje om het te wissen.
 * @param props De eigenschap, de stand van het filter en wat er bij klikken en wissen gebeurt.
 * @returns De chip.
 */
export function FilterChip(props: ChipProps) {
  return props.eigenschap.filter?.soort === 'referentie' && props.stand.modus === 'waarde' ? (
    <ReferentieFilterChip {...props} filter={props.eigenschap.filter} />
  ) : (
    <Filterchip {...props} tekst={samenvatting(props.eigenschap, props.stand)} />
  );
}

function ReferentieFilterChip(props: ChipProps & { filter: Extract<Eigenschapfilter, { soort: 'referentie' }> }) {
  const query = useReferentielijst(props.filter.lijst);
  const namen = new Map((query.data ?? []).map((item) => [item.id, item.naam]));
  return <Filterchip {...props} tekst={samenvatting(props.eigenschap, props.stand, (id) => namen.get(id) ?? '…')} />;
}

function Filterchip({ eigenschap, onKlik, onWis, tekst }: ChipProps & { tekst: string }) {
  return (
    <Chip
      size="small"
      color="primary"
      variant="outlined"
      label={`${eigenschap.label}: ${tekst}`}
      onClick={(gebeurtenis) => onKlik(gebeurtenis.currentTarget)}
      onDelete={onWis}
      sx={{ maxWidth: 360 }}
    />
  );
}
